import React from 'react'
import { usePhoneViewport } from '@/hooks/useMediaQuery'
import SortIcon from '@/assets/svgs/sort-icon.svg'
import SortIconActive from '@/assets/svgs/sort-icon-active.svg'
import SortIconActiveUp from '@/assets/svgs/sort-icon-active-up.svg'
import IconNoMessage from '@/assets/svgs/no-message.svg'
import DownloadIcon from '@/assets/svgs/download-large.svg'
import Button from '@/components/Button/Button'
import { ButtonType, ButtonVariant } from '@/components/Button/Button.type'
import Pagination from '@/components/Pagination/index'
import Loader from '@/components/Loader'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import useFeature, { features } from '@/hooks/useFeature'

const ACTION_KEY = 'action'

const getColumnStyle = (headerKey, isMobile) => {
  if (headerKey === 'project_contract_name')
    return isMobile
      ? { flex: '0 0 200px', minWidth: '200px', maxWidth: '200px' }
      : { flex: '0 0 320px', minWidth: '320px', maxWidth: '320px' }
  if (headerKey === ACTION_KEY) return { flex: '0 0 80px', minWidth: '80px', maxWidth: '80px' }
  if (headerKey === 'status' && isMobile)
    return { flex: '0 0 200px', minWidth: '200px', maxWidth: '200px' }
  return { flex: 1, minWidth: '120px' }
}

/**
 * The columns to draw, from whichever of the two props was given.
 *
 * `headers` is the original shape — a `{ key: label }` map whose every column is
 * sortable and whose cells go through `formatValue`. `columns` is the shape a
 * config-driven listing needs: order, per-column sortability and a per-column
 * renderer, none of which a plain map can express. `columns` wins when both are
 * passed.
 *
 * An `action` column keeps its special handling in either shape: it is the
 * per-row download button, not a field.
 */
const toColumnList = (columns, headers) => {
  if (Array.isArray(columns) && columns.length > 0) {
    return columns.map(column => ({
      key: column.key,
      label: column.label ?? '',
      // Opt-in, unlike the `headers` shape where every column sorts. The key
      // doubles as the backend sort column, so a column that has not been
      // declared sortable must not offer a control the backend would reject.
      sortable: column.key !== ACTION_KEY && column.sortable === true,
      render: column.render,
      // Overrides the per-key widths below. Those name EMS's own columns, and a
      // config-driven or uploaded table's keys come from a schema that happens
      // to use the same names for fields of a different size.
      style: column.style,
    }))
  }

  return Object.keys(headers || {}).map(key => ({
    key,
    label: headers[key],
    sortable: key !== ACTION_KEY,
    render: undefined,
    style: undefined,
  }))
}

const MasterDataTable = ({
  headers,
  columns,
  data,
  currentPage = 1,
  totalPages = 1,
  totalItems = 0,
  pageLimit = 10,
  onPageChange,
  onSort,
  sortColumn,
  sortOrder,
  getClassName,
  formatValue = value => value,
  emptyMessage = 'No Data Found!!!',
  emptyIcon,
  errorMessage,
  onRowClick,
  onDownloadCsv,
  loading = false,
  loadingContent = <Loader />,
  bare = false,
}) => {
  const isMobile = usePhoneViewport()
  const isPhase2 = useFeature(features.SUBMITTALS_PHASE_2)
  const processedData = data || []
  const columnList = toColumnList(columns, headers)

  const handleSort = headerKey => {
    if (onSort) {
      onSort(headerKey)
    }
  }

  const getPageLabel = (total, limit, current) => {
    const start = current * limit + 1
    const end = Math.min((current + 1) * limit, total)
    return `${start}-${end}`
  }

  const list = processedData?.map((item, index) => {
    return (
      <div
        /* min-h, not h: the cells below wrap (`break-words`), and a fixed height
           clips a value that takes a second line rather than growing the row —
           the same reason the header row uses a minimum.
           items-stretch, as the header row does, so the cells fill the row's full
           height and each one centres its own content within it. Centring here
           instead would centre the whole block of cells and leave their bottom
           border floating above the row's real bottom edge, which reads as a row
           whose text sits high. */
        className="master-data-table__table__body__row flex w-full min-h-[76px] items-stretch"
        key={index}
      >
        <div className="flex w-full cursor-pointer" onClick={() => onRowClick && onRowClick(item)}>
          {columnList.map(({ key: headerKey, render, style }) => (
            <div
              key={headerKey}
              className={clsx(
                // py-2, matching the header cells: a value tall enough to set the
                // row's height would otherwise sit flush against the dividers
                // above and below it. Costs nothing on the rows that exist today
                // — body-2 is a 20px line, so three lines plus this padding come
                // to exactly the 76px minimum, and only a fourth line grows the
                // row.
                'box-border flex items-center border-b border-grey-90 type-body-2 break-words py-2 pr-2 pl-6',
                headerKey === ACTION_KEY && 'justify-center p-0 pointer-events-none'
              )}
              style={style ?? getColumnStyle(headerKey, isMobile)}
              data-testid={headerKey}
              onClick={e => headerKey === ACTION_KEY && e.stopPropagation()}
            >
              {headerKey === ACTION_KEY ? (
                <div
                  className="flex justify-center items-center w-full pointer-events-auto"
                  onClick={e => e.stopPropagation()}
                >
                  <Button
                    type={ButtonType.GHOST}
                    variant={ButtonVariant.STANDARD}
                    prefixIcon={<img src={DownloadIcon} alt="Download" />}
                    onClick={() => onDownloadCsv && onDownloadCsv(item)}
                    disabled={loading}
                  />
                </div>
              ) : (
                // min-w-0, or this stays a flex item at its automatic minimum
                // size — min-content — and a value wider than its column spills
                // over the next one rather than wrapping. The cell's break-words
                // never got the chance to apply.
                <div className="min-w-0">
                  {render ? render(item) : formatValue(item?.[headerKey], headerKey)}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    )
  })

  return (
    /* `bare` drops the card the table normally draws for itself, for a table
       already inside one — nesting the two would put a rounded border inside a
       rounded border. Opt-in, so every existing listing keeps its own card.

       The card itself does not scroll — only the header-and-rows block inside it
       does. Both carried `overflow-x-auto` before, which gave a wide table two
       nested scrollbars and, worse, put the pager inside the outer one: the paging
       controls slid sideways along with the columns. */
    <div className={clsx('relative', !bare && 'border border-grey-90 rounded-xl bg-white')}>
      <div>
        <div className="type-body-2 overflow-x-auto">
          <div>
            {/* min-h, not a fixed h: a config-driven table's labels come from a
                schema and can be long enough to wrap onto three or four lines
                ("Civil and Structural Engineering Consultant Company Unique
                Entity Number"), which a fixed height clips into the first data
                row. Short labels still measure 56.5px, so the existing tables
                are unchanged. */}
            <div className="flex w-full min-h-[56.5px] items-stretch">
              <div className="flex w-full">
                {columnList.map(({ key: headerKey, label, sortable, style }) => (
                  <div
                    key={headerKey}
                    data-id={headerKey}
                    className={clsx(
                      'box-border flex items-center justify-between relative py-2 pr-4 pl-6 border-b border-grey-90',
                      "after:content-[''] after:absolute after:right-0 after:top-1/2 after:-translate-y-1/2 after:h-1/2 after:w-px after:bg-grey-90 last:after:hidden",
                      headerKey === ACTION_KEY && 'justify-center',
                      sortable ? 'cursor-pointer' : 'cursor-default',
                      sortColumn === headerKey ? 'text-theme-50' : 'text-grey-50',
                      getClassName?.(headerKey)
                    )}
                    style={style ?? getColumnStyle(headerKey, isMobile)}
                    onClick={sortable ? () => handleSort?.(headerKey) : null}
                    onKeyDown={sortable ? () => handleSort?.(headerKey) : null}
                    id={headerKey}
                    role="none"
                  >
                    <div>{label}</div>
                    {sortable && (
                      // shrink-0: the arrows are a flex sibling of the label, so
                      // without it a heading long enough to wrap shrinks them.
                      <div className={clsx('leading-none ml-2', isPhase2 && 'shrink-0')}>
                        <img
                          src={
                            sortColumn === headerKey
                              ? sortOrder === 'desc'
                                ? SortIconActive
                                : SortIconActiveUp
                              : SortIcon
                          }
                          alt="Sort"
                          className="sort-icon w-4 h-4 cursor-pointer"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            {!loading && !errorMessage && processedData?.length > 0 && list}
          </div>
        </div>
      </div>
      {loading && (
        <div
          className={clsx(
            'pt-[42px] pb-[44px] bg-grey-95 flex items-center justify-center',
            !bare && 'rounded-b-xl'
          )}
        >
          {loadingContent}
        </div>
      )}
      {/* A failed fetch, kept apart from the empty state: "no submissions" is a
          statement about the data, which a request that never came back cannot
          make. Drawn without the empty icon, since it is not an empty result. */}
      {!loading && errorMessage && (
        <div className={clsx('pt-[42px] px-8 pb-[44px] bg-grey-95', !bare && 'rounded-b-xl')}>
          <div className="flex items-center flex-col text-grey-50 text-center">{errorMessage}</div>
        </div>
      )}
      {!loading && !errorMessage && processedData?.length === 0 && (
        <div className={clsx('pt-[42px] px-8 pb-[44px] bg-grey-95', !bare && 'rounded-b-xl')}>
          <div className="flex items-center flex-col text-grey-50 text-center [&_svg]:mb-2">
            {emptyIcon ?? <img src={IconNoMessage} alt="No message" />}
            <div>{emptyMessage}</div>
          </div>
        </div>
      )}
      {!loading && !errorMessage && processedData?.length > 0 && (
        <Pagination
          totalItems={totalItems}
          pageLimit={pageLimit}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
          getPageLabel={getPageLabel}
        />
      )}
    </div>
  )
}

MasterDataTable.propTypes = {
  // One of headers / columns is required; columns wins when both are given.
  headers: PropTypes.object,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.node,
      sortable: PropTypes.bool,
      render: PropTypes.func,
      style: PropTypes.object,
    })
  ),
  data: PropTypes.array.isRequired,
  currentPage: PropTypes.number,
  totalPages: PropTypes.number,
  totalItems: PropTypes.number,
  pageLimit: PropTypes.number,
  onPageChange: PropTypes.func,
  onSort: PropTypes.func,
  sortColumn: PropTypes.string,
  sortOrder: PropTypes.string,
  getClassName: PropTypes.func,
  formatValue: PropTypes.func,
  emptyMessage: PropTypes.string,
  emptyIcon: PropTypes.node,
  /**
   * Replaces both the rows and the empty state when the fetch failed.
   *
   * Named `errorMessage` rather than `error` deliberately: the EMS listing has
   * passed an `error` prop since before this table had anywhere to put one, so
   * it has always been dropped, and a table that suddenly honoured it would
   * change how that page reports a failed fetch. It reads the same as it does in
   * production; only the listings that ask for this slot by name get it.
   */
  errorMessage: PropTypes.node,
  onRowClick: PropTypes.func,
  onDownloadCsv: PropTypes.func,
  loading: PropTypes.bool,
  loadingContent: PropTypes.node,
  bare: PropTypes.bool,
}

export default MasterDataTable
